import { Injectable, NgZone } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { LoggingService } from '../services/logging.service';
import { OperationState } from '../store/operation/operation.state';

@Injectable({ providedIn: 'root' })
export class AuthGuard {
  constructor(
    private readonly router: Router,
    private readonly authService: AuthService,
    private readonly zone: NgZone,
    protected readonly loggingService: LoggingService,
    private readonly store: Store,
  ) {}

  @Select(OperationState.getPlatformRolesOperations)
  private readonly platformRolesOperations$: Observable<string[]>;

  canActivateChild(childRoute: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> {
    if (!this.authService.userIsLogged()) {
      this.authService.setRedirectUrl(state.url);
      return of(this.router.parseUrl('/login'));
    }

    // Given the new implementation of the routing we needed to add this piece so to consider only
    // the last step of the route, otherwise the authGard would check at each section if the user
    // was allowed to see page or not. This however, would create problem of permission denied since that
    // for reaching an allowed page, the user might path over a page in which does not have permission
    if (childRoute.children.length > 0) {
      return of(true);
    }

    const { operation } = childRoute.data;

    if (!operation) {
      return of(false);
    }

    return this.platformRolesOperations$.pipe(
      map((operations) => operations.includes(operation)),
      tap((isAllowed) => {
        // if (isAllowed) {
        //   // Commented out so to avoid logging information about simple navigation between pages, view-page logs
        //   this.store.dispatch(
        //     new PostLogAction({
        //       operation,
        //       application,
        //       study: '',
        //       success: true,
        //       entityValue: 'Authorization Guard',
        //     })
        //   );
        // }
      }),
      map((isAllowed) => {
        if (isAllowed) {
          return true;
        } else {
          this.router.navigate(['/error/permission-denied'], {
            state: {
              error: {
                message: `"${operation}" operation is not allowed for this child route`,
              },
            },
          });
          return false;
        }
      }),
      catchError(() => {
        this.router.navigate(['/error']);
        return of(false);
      }),
    );
  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    if (this.authService.userIsLogged()) {
      return true;
    }

    this.authService.setRedirectUrl(state.url);
    return this.router.createUrlTree(['/login']);
  }
}
